/**
 * Boundary files for the enrollee map (public/geo, built from OCHA/OSGOF LGAs and GRID3 wards):
 * geo/index.json (state slugs and aliases), geo/lga/<state>.json, geo/ward/<state>.json. Each feature is
 * { name, lga?, path } with an SVG path in Highcharts map units. Names are matched loosely to the database.
 */
const cache = {};

function fetchJson(url) {
  if (!cache[url]) {
    cache[url] = fetch(url).then((r) => {
      if (!r.ok) throw new Error(`Could not load ${url}`);
      return r.json();
    });
  }
  return cache[url];
}

export function normalise(name) {
  return String(name || "")
    .toLowerCase()
    .replace(/\b(local government area|local government|lga|ward|wards)\b/g, " ")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "");
}

function distance(a, b) {
  const m = [];
  for (let i = 0; i <= b.length; i++) m[i] = [i];
  for (let j = 0; j <= a.length; j++) m[0][j] = j;
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      m[i][j] = b[i - 1] === a[j - 1] ? m[i - 1][j - 1] : Math.min(m[i - 1][j - 1] + 1, m[i][j - 1] + 1, m[i - 1][j] + 1);
    }
  }
  return m[b.length][a.length];
}

/** The feature for a database name: exact after normalising, else the closest within a small edit distance. */
export function matchFeature(features, name, lgaName) {
  const key = normalise(name);
  if (!key) return null;
  let pool = features;
  if (lgaName) {
    const lgaKey = normalise(lgaName);
    const inLga = features.filter((f) => normalise(f.lga) === lgaKey);
    if (inLga.length) pool = inLga;
  }
  const exact = pool.find((f) => normalise(f.name) === key);
  if (exact) return exact;
  let best = null;
  let bestD = Infinity;
  pool.forEach((f) => {
    const d = distance(normalise(f.name), key);
    if (d < bestD) { bestD = d; best = f; }
  });
  return bestD <= Math.max(1, Math.floor(key.length / 5)) ? best : null;
}

export async function stateSlug(stateName) {
  const index = await fetchJson("/geo/index.json");
  const key = String(stateName || "").toLowerCase().replace(/\bstate\b/g, "").trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  if (index.states[key]) return key;
  if (index.aliases[key]) return index.aliases[key];
  const found = Object.keys(index.states).find((s) => key.includes(s) || s.includes(key));
  return found || null;
}

export function loadBoundaries(level, slug) {
  return fetchJson(`/geo/${level === "ward" ? "ward" : "lga"}/${slug}.json`).then((d) => d.features);
}

export function attribution() {
  return fetchJson("/geo/index.json").then((d) => d.attribution);
}
